// Reason: Keeping the typing easy for generic behavior.
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Timer } from './timer';

/**
 * ## cortex:std:retry
 * Module for retrying a given function either x amount of times
 * or every x seconds
 */
export namespace Retry {
	interface TheOptions {
		againAfter: number;
		for: number;
		hooks: Array<() => void>;
	}

	interface BruteForceOptions {
		every: number;
		for: number;
		hooks: Array<() => void>;
	}

	/** Retry a function every x amount of seconds, regardless of success or failure */
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	export const byBruteForce = <T extends (...args: any) => any>(
		fn: T,
		options?: Partial<BruteForceOptions>,
	): Promise<ReturnType<T>> => {
		const defaultOption: BruteForceOptions = {
			every: 1000,
			for: 1,
			hooks: [],
			...(options ? options : {}),
		};

		return new Promise((resolve, reject) => {
			let attempts = 0;
			let finishedRequest = false;

			const disposer = Timer.repeat(
				() => {
					proxy(++attempts);
				},
				defaultOption.every,
				true,
			);

			async function proxy(attempt: number): Promise<void> {
				if (attempt > defaultOption.for && !finishedRequest) {
					disposer();

					reject(`request did not finish in ${defaultOption.for} attempts`);
				} else {
					try {
						const resolvedIntoThis = await fn();
						if (!finishedRequest) {
							disposer();
							finishedRequest = true;

							resolve(resolvedIntoThis);
						}
						// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
					} catch (error) {
						defaultOption.hooks?.forEach((hook) => hook());
					}
				}
			}
		});
	};

	/** Retry a function for a given amount of times in case if fails */
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	export const the = <T extends (...args: any) => any>(fn: T, options?: Partial<TheOptions>): Promise<ReturnType<T>> => {
		const defaultOption: TheOptions = {
			againAfter: 1000,
			for: 1,
			hooks: [],
			...(options ? options : {}),
		};

		return new Promise((resolve, reject) => {
			let attempts = 0;

			async function proxy(attempt: number): Promise<void> {
				try {
					const resolvedIntoThis = await fn();

					resolve(resolvedIntoThis);
					// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
				} catch (error) {
					defaultOption.hooks?.forEach((hook) => hook());

					if (attempt < defaultOption.for) {
						retry();
					} else {
						reject(`request did not finish in ${defaultOption.for} attempts`);
					}
				}
			}

			function retry(): void {
				setTimeout(() => {
					proxy(++attempts);
				}, defaultOption.againAfter);
			}

			proxy(++attempts);
		});
	};
}
